import { Controller } from "@hotwired/stimulus"

// Public e-signature controller for ExternalSigningRequest.
//
// Mirrors the tenancy `pdf-signing` controller but trimmed to the single-
// signer, no-login flow: one signature field, name typed in the modal,
// consent in the modal, submit via the public submit endpoint.
export default class extends Controller {
  static targets = [
    "pagesContainer",
    "signatureModal",
    "drawTab",
    "typeTab",
    "drawMode",
    "typeMode",
    "signaturePad",
    "typeInput",
    "typePreview",
    "signedNameInput",
    "consentCheckbox",
    "insertButton",
    "loadingOverlay",
    "finishBanner"
  ]

  static values = {
    pdfUrl:    String,
    submitUrl: String,
    positions: Array,
    signerName: String
  }

  async connect() {
    this.signatureData    = null
    this.signatureMethod  = 'draw'
    this.signaturePadInstance = null
    this.activePosition   = null
    this.pdfScale         = 1
    this.signedAt         = null

    await this.loadPdf()
    this.initSignaturePad()
  }

  // ---- PDF rendering -------------------------------------------------------

  async loadPdf() {
    try {
      if (!window.pdfjsLib) await this._loadPdfjsFromCdn()
      const pdfjsLib = window.pdfjsLib
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'

      const response    = await fetch(this.pdfUrlValue)
      const arrayBuffer = await response.arrayBuffer()
      const pdf         = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      this.pdf          = pdf
      this.totalPages   = pdf.numPages

      this.pagesContainerTarget.innerHTML = ''
      for (let p = 1; p <= this.totalPages; p++) {
        await this._renderPage(p)
      }
    } catch (error) {
      console.error('Error loading PDF:', error)
      this.pagesContainerTarget.innerHTML = `
        <div class="text-center text-white py-20">
          <p class="text-red-400">Failed to load document. Please refresh and try again.</p>
        </div>`
    }
  }

  async _renderPage(pageNum) {
    const page         = await this.pdf.getPage(pageNum)
    const scale        = this._calculateScale(page)
    this.pdfScale      = scale
    const viewport     = page.getViewport({ scale })
    const pixelRatio   = window.devicePixelRatio || 1

    const wrapper       = document.createElement('div')
    wrapper.className   = 'pdf-page-wrapper'

    const pageContainer = document.createElement('div')
    pageContainer.className     = 'pdf-page'
    pageContainer.style.width   = `${viewport.width}px`
    pageContainer.style.height  = `${viewport.height}px`
    pageContainer.dataset.pageNumber = pageNum

    const canvas  = document.createElement('canvas')
    const context = canvas.getContext('2d')
    canvas.width  = viewport.width * pixelRatio
    canvas.height = viewport.height * pixelRatio
    canvas.style.width  = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`
    context.scale(pixelRatio, pixelRatio)

    await page.render({ canvasContext: context, viewport }).promise

    pageContainer.appendChild(canvas)
    this._addOverlays(pageContainer, pageNum)
    wrapper.appendChild(pageContainer)
    this.pagesContainerTarget.appendChild(wrapper)
  }

  _calculateScale(page) {
    const containerWidth = this.pagesContainerTarget.clientWidth - 32
    const natural        = page.getViewport({ scale: 1 })
    const fit            = containerWidth / natural.width
    return Math.min(fit, 1.5)
  }

  _addOverlays(pageContainer, pageNum) {
    const pagePositions = (this.positionsValue || []).filter(p => p.pageNumber === pageNum)
    pagePositions.forEach(position => {
      if (position.fieldType !== 'signature' && position.fieldType !== 'date') return

      const overlay = document.createElement('div')
      overlay.className = 'signature-overlay'
      overlay.style.left   = `${position.xPercent}%`
      overlay.style.top    = `${position.yPercent}%`
      overlay.style.width  = `${position.widthPercent}%`
      overlay.style.height = `${position.heightPercent}%`

      if (position.fieldType === 'signature') {
        overlay.classList.add('current-user')
        overlay.innerHTML = '<span class="signature-overlay-label">Click to sign</span>'
        overlay.addEventListener('click', () => this._openModal(position, overlay))
        overlay.dataset.role = 'signature-target'
      } else if (position.fieldType === 'date') {
        overlay.style.display = 'none'
        overlay.dataset.role = 'date-target'
      }

      pageContainer.appendChild(overlay)
    })
  }

  // ---- Signature pad -------------------------------------------------------

  initSignaturePad() {
    if (!this.hasSignaturePadTarget) return
    import('signature_pad').then(({ default: SignaturePad }) => {
      const canvas = this.signaturePadTarget
      this.signaturePadInstance = new SignaturePad(canvas, {
        // Transparent background so the exported PNG contains only the
        // ink strokes — the stamp on the final PDF won't cover adjacent
        // cells with an opaque white rectangle.
        backgroundColor: 'rgba(255,255,255,0)',
        penColor:        'rgb(0,0,0)'
      })
      this._resizeSignaturePad()
      window.addEventListener('resize', () => this._resizeSignaturePad())
    })
  }

  _resizeSignaturePad() {
    if (!this.signaturePadInstance || !this.hasSignaturePadTarget) return
    const canvas = this.signaturePadTarget
    const ratio  = Math.max(window.devicePixelRatio || 1, 1)
    const rect   = canvas.getBoundingClientRect()
    canvas.width  = rect.width * ratio
    canvas.height = 200 * ratio
    canvas.getContext('2d').scale(ratio, ratio)
    this.signaturePadInstance.clear()
  }

  // ---- Modal interactions --------------------------------------------------

  _openModal(position, overlay) {
    this.activePosition = position
    this.activeOverlay  = overlay
    this.signatureModalTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    this.switchToDrawMode()
    this.clearSignature()
    // Pre-fill name with what we have on file (signer can overwrite).
    if (this.hasSignedNameInputTarget && !this.signedNameInputTarget.value) {
      this.signedNameInputTarget.value = this.signerNameValue || ''
    }
    setTimeout(() => this._resizeSignaturePad(), 50)
  }

  closeSignatureModal() {
    this.signatureModalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    this.activePosition = null
  }

  switchToDrawMode() {
    this.signatureMethod = 'draw'
    this.drawTabTarget.classList.add('text-gray-900', 'border-gray-900')
    this.drawTabTarget.classList.remove('text-gray-500', 'border-transparent')
    this.typeTabTarget.classList.add('text-gray-500', 'border-transparent')
    this.typeTabTarget.classList.remove('text-gray-900', 'border-gray-900')
    this.drawModeTarget.classList.remove('hidden')
    this.typeModeTarget.classList.add('hidden')
    setTimeout(() => this._resizeSignaturePad(), 50)
  }

  switchToTypeMode() {
    this.signatureMethod = 'type'
    this.typeTabTarget.classList.add('text-gray-900', 'border-gray-900')
    this.typeTabTarget.classList.remove('text-gray-500', 'border-transparent')
    this.drawTabTarget.classList.add('text-gray-500', 'border-transparent')
    this.drawTabTarget.classList.remove('text-gray-900', 'border-gray-900')
    this.typeModeTarget.classList.remove('hidden')
    this.drawModeTarget.classList.add('hidden')
  }

  clearSignature() {
    if (this.signaturePadInstance) this.signaturePadInstance.clear()
    if (this.hasTypeInputTarget) this.typeInputTarget.value = ''
    this._renderTypePreview('')
    this.signatureData = null
  }

  onTypeInput() {
    this._renderTypePreview(this.typeInputTarget.value)
  }

  _renderTypePreview(text) {
    if (!this.hasTypePreviewTarget) return
    if (text.trim().length === 0) {
      this.typePreviewTarget.innerHTML = '<span class="text-gray-300">Your signature</span>'
    } else {
      this.typePreviewTarget.textContent = text
    }
  }

  async insertSignature() {
    const name    = (this.hasSignedNameInputTarget ? this.signedNameInputTarget.value : '').trim()
    const consent = this.hasConsentCheckboxTarget && this.consentCheckboxTarget.checked
    if (!name)    { alert('Please type your full name.');  return }
    if (!consent) { alert('Please tick the box to confirm you intend to sign.'); return }

    // Capture signature image.
    if (this.signatureMethod === 'draw') {
      if (!this.signaturePadInstance || this.signaturePadInstance.isEmpty()) {
        alert('Please draw your signature, or switch to Type mode.')
        return
      }
      this.signatureData = this.signaturePadInstance.toDataURL('image/png')
    } else {
      const typed = (this.hasTypeInputTarget ? this.typeInputTarget.value : '').trim()
      if (!typed) { alert('Please type your signature.'); return }
      this.signatureData = this._typedToDataUrl(typed)
    }

    // Stamp the signature preview onto the overlay.
    if (this.activeOverlay) {
      this.activeOverlay.classList.remove('current-user')
      this.activeOverlay.classList.add('signed')
      this.activeOverlay.innerHTML = ''
      const img = document.createElement('img')
      img.src = this.signatureData
      img.alt = 'Signature'
      this.activeOverlay.appendChild(img)
    }

    // Auto-stamp the date overlay if present (same row, signer index).
    this._stampDateOverlay()

    this.closeSignatureModal()
    this._submit(name)
  }

  _typedToDataUrl(text) {
    const canvas = document.createElement('canvas')
    canvas.width  = 600
    canvas.height = 200
    const ctx     = canvas.getContext('2d')
    // Skip white fill — leave the canvas transparent so the exported PNG
    // only contains the rendered text strokes.
    ctx.fillStyle = 'black'
    ctx.font      = "italic 72px 'Dancing Script', cursive"
    ctx.textBaseline = 'middle'
    ctx.fillText(text, 20, 100)
    return canvas.toDataURL('image/png')
  }

  _stampDateOverlay() {
    const dateOverlay = this.element.querySelector('[data-role="date-target"]')
    if (!dateOverlay) return
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    dateOverlay.style.display = ''
    dateOverlay.classList.add('signed')
    dateOverlay.innerHTML = `<span class="date-text">${today}</span>`
  }

  // ---- Submission ----------------------------------------------------------

  async _submit(signedName) {
    this.loadingOverlayTarget.classList.remove('hidden')
    try {
      const fd = new FormData()
      fd.set('signature_data', this.signatureData)
      fd.set('signed_name', signedName)
      fd.set('consent', '1')
      const resp = await fetch(this.submitUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept':       'application/json'
        },
        body: fd
      })
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}))
        if (data.redirect_url) {
          window.location.href = data.redirect_url
          return
        }
      }
      // Fallback: assume redirect not provided, hard reload current location.
      window.location.reload()
    } catch (e) {
      console.error('Signature submission failed:', e)
      this.loadingOverlayTarget.classList.add('hidden')
      alert('We could not submit your signature. Please try again.')
    }
  }

  _loadPdfjsFromCdn() {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src   = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
      s.onload  = resolve
      s.onerror = reject
      document.head.appendChild(s)
    })
  }
}
